import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@reach/dialog';
import { Document, Page, pdfjs } from 'react-pdf';
import { TextOnly } from './Text';
import LoaderButton from './LoaderButton';
import { Loading } from './Loading';
import GenericPopover from './GenericPopover';
import { TaxExemptRequest } from '../types';
import { Typeahead } from 'react-bootstrap-typeahead';
import { getToolTaxExemptFileURL } from '../libs/db-lib';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { addApostropheToString } from '../libs/utils';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type TaxExemptModalProps = {
  showModal: boolean;
  handleCancel: () => void;
  taxExemptRequest: TaxExemptRequest | null;
  handleApprove: (teRequestID: string, userID: string, exemptionReason: string, zendeskTicketID: string, skipRefunds: boolean) => Promise<any>;
  handleReject: (teRequestID: string, userID: string, reason: string, zendeskTicketID: string) => Promise<any>;
  loadingActions: string;
  exemptionReasons: { id: string; name: string }[];
  actionType?: 'APPROVE' | 'REJECT' | 'BOTH';
};

const TaxExemptModal = ({
  showModal,
  handleCancel,
  taxExemptRequest,
  handleApprove,
  handleReject,
  loadingActions,
  exemptionReasons,
  actionType = 'BOTH',
}: TaxExemptModalProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [docError, setDocError] = useState<boolean>(false);
  const [docUrl, setDocUrl] = useState<string | undefined>();
  const [docType, setDocType] = useState<string | undefined>();
  const [docBlob, setDocBlob] = useState<Blob | null>(null);
  const [docLoading, setDocLoading] = useState<boolean>(true);
  const [pageWidth, setPageWidth] = useState<number>(500);
  const [selectedReason, setSelectedReason] = useState<{ id: string; name: string } | null>(
    (taxExemptRequest?.reasonID && taxExemptRequest?.exemptionReason) ?
    {id: taxExemptRequest.reasonID, name: taxExemptRequest.exemptionReason}:
    null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const pdfContainerRef = React.useRef<HTMLDivElement>(null);

  const [skipRefunds, setSkipRefunds] = useState<boolean>(false);

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [disableGetShopSummaryBtn, setDisableGetShopSummaryBtn] = useState<boolean>(true);

  const history = useHistory();

  const handleCloseUnsavedChangesModal = () => {
    document.querySelector('.c-modal.unsaved-changes')?.classList.add('closed');
    setTimeout(() => {
      setShowUnsavedChangesModal(false);
    }, 350);
  }

  const handleGetShopSummary = () => {
    const redirect='/shopFunctions/getShopSummary';
    const urlParams=`?query=${taxExemptRequest?.shopID}&queryType=SHOP+ID`;
    const url = `${redirect}${urlParams}`;
    history.push(url);
  }

  useEffect(() => {
    if (showUnsavedChangesModal) {
      setTimeout(() => {
        setDisableGetShopSummaryBtn(false);
      }, 1500);
    } else {
      setDisableGetShopSummaryBtn(true);
    }
  }, [showUnsavedChangesModal]);

  useEffect(() => {
    setSelectedReason(
      (taxExemptRequest?.reasonID && taxExemptRequest?.exemptionReason) ?
      {id: taxExemptRequest.reasonID, name: taxExemptRequest.exemptionReason}:
      null
    );
  }, [exemptionReasons, taxExemptRequest]);

  // Update page width when container resizes (accounting for padding)
  const updatePageWidth = useCallback(() => {
    if (pdfContainerRef.current) {
      const containerWidth = pdfContainerRef.current.getBoundingClientRect().width;
      const styles = window.getComputedStyle(pdfContainerRef.current);
      const paddingLeft = parseFloat(styles.paddingLeft);
      const paddingRight = parseFloat(styles.paddingRight);
      setPageWidth(containerWidth - paddingLeft - paddingRight);
    }
  }, []);

  useEffect(() => {
    if (!taxExemptRequest) return;

    let rafId: number | null = null;

    // Set up ResizeObserver for dynamic updates
    const resizeObserver = new ResizeObserver(() => {
      // Cancel any pending animation frame and schedule a new one
      // This is supposed to fix the "ResizeObserver loop completed with undelivered notifications" error
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updatePageWidth();
        rafId = null;
      });
    });

    if (pdfContainerRef.current) {
      resizeObserver.observe(pdfContainerRef.current);
    }

    // Initial measurement after paint to ensure container is laid out
    requestAnimationFrame(() => {
      updatePageWidth();
    });

    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [taxExemptRequest, updatePageWidth]);

  // Reset state when modal closes
  useEffect(() => {
    if (!showModal) return;

    return () => {
      setSelectedReason(null);
      setRejectionReason('');
    };
  }, [showModal]);

  // Populate fields for approved/rejected requests
  useEffect(() => {
    if (!taxExemptRequest) return;

    if (taxExemptRequest.status === 'APPROVED' && taxExemptRequest.exemptionReason) {
      const matchingReason = exemptionReasons.find(r => r.id === taxExemptRequest.exemptionReason);
      if (matchingReason) {
        setSelectedReason(matchingReason);
      }
    } else if (taxExemptRequest.status === 'REJECTED' && taxExemptRequest.rejectionReason) {
      setRejectionReason(taxExemptRequest.rejectionReason);
    }
  }, [taxExemptRequest, exemptionReasons]);

  // Fetch PDF from S3 using docKey
  useEffect(() => {
    if (!taxExemptRequest?.docKey) return;

    setDocLoading(true);
    setDocBlob(null);

    const fetchTaxExemptDoc = async () => {
      try {
        const url = await getToolTaxExemptFileURL(taxExemptRequest.docKey);
        const response = await fetch(url);
        const blob = await response.blob();
        setDocUrl(url);
        setDocType(blob.type);
        setDocBlob(blob);
        setDocLoading(false);
      } catch (err) {
        setDocError(true);
        setDocLoading(false);
      }
    }
    fetchTaxExemptDoc();

  }, [taxExemptRequest?.docKey]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  function handleDownloadDocument() {
    if (!docBlob || !docUrl) return;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(docBlob);
    const extension = docType?.split('/')[1] || 'bin';
    const sanitizedShopName = (taxExemptRequest?.shopName ?? '').replace(/[<>:"/\\|?*]/g, '_');
    link.download = `tax-exempt-certificate-${sanitizedShopName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  const downloadDocumentBtn = (
    <div className='u-margin-left-auto'>
      <button className="c-btn c-btn-outline u-margin-left" disabled={docLoading} onClick={() => handleDownloadDocument()}>
        <div className="c-btn__inner">
          <i className="c-btn__icon fa fa-download" />
          Download Document
        </div>
      </button>
    </div>
  );

  function handleCancelModal() {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      handleCancel();
    }, 350);
  }

  if (!taxExemptRequest) {
    return null;
  }

  const isLoading = loadingActions === taxExemptRequest.teRequestID;
  const isReadOnly = taxExemptRequest.status === 'APPROVED' || taxExemptRequest.status === 'REJECTED';
  const showTypeahead = actionType !== 'REJECT' && taxExemptRequest.status !== 'REJECTED';

  return (
    <Dialog
      isOpen={showModal}
      onDismiss={handleCancelModal}
      className="c-modal large tax-exempt-modal"
      aria-label={'tax exempt request modal'}
    >
      <button className="c-btn-icon c-modal__close" onClick={handleCancelModal}>
        <div className="c-btn__inner">
          <i className="c-btn__icon fa fa-times" />
        </div>
      </button>

      <div className="c-modal__body u-margin-top-large">
        <h2 className="u-margin-bottom-medium">Tax Exempt Request</h2>

        {/* Shop Name */}
        <div className="l-flex-row u-align-center u-margin-bottom-small">
          <span className="recovery-variable">Shop Name </span>
          <span>
            {taxExemptRequest?.shopID
              ? <div className='l-flex-row u-align-center'>
                <div className='clickable' onClick={() => setShowUnsavedChangesModal(true)}>
                  {taxExemptRequest.shopName}
                </div>

                <button
                  className="c-btn-icon"
                  onClick={() => {
                    navigator.clipboard.writeText(taxExemptRequest.shopName).then(function () {
                      toast.success('Copied!', {
                        containerId: 'standard',
                      });
                    });
                  } }
                >
                  <div className="c-btn__inner">
                    <span className="c-btn__icon fa fa-clipboard" title="Copy" />
                  </div>
                </button>
              </div>
              : <span>{taxExemptRequest.shopName}</span>}
          </span>
        </div>

        {/* Shop Address */}
        <div className="u-margin-bottom-small">
          <span className="recovery-variable">Shop Address </span>
          <span>{taxExemptRequest.address.streetAddress1}{taxExemptRequest.address.streetAddress2 ? `, ${taxExemptRequest.address.streetAddress2}` : ''}, {taxExemptRequest.address.city}, {taxExemptRequest.address.state} {taxExemptRequest.address.zip}</span>
        </div>

        {/* Shop Phone Number */}
        <div className="u-margin-bottom-small">
          <span className="recovery-variable">Shop Phone </span>
          <span>{taxExemptRequest.phoneNumber}</span>
        </div>

        {/* Owner Email */}
        <div className="u-margin-bottom-small">
          <span className="recovery-variable">Owner Email </span>
          <span>{taxExemptRequest.email}</span>
        </div>

        {/* Taxpayer ID Number */}
        <div className="u-margin-bottom-small">
          <span className="recovery-variable">Taxpayer ID Number </span>
          <span>{taxExemptRequest.taxpayerID}</span>
        </div>

        {/* Exemption Reason Selector */}
        {showTypeahead && (
          <>
            <div className="c-field u-margin-top-large">
              <label htmlFor="exemptionReason" className="c-field__label">Exemption Reason</label>
              {exemptionReasons.length !== 0 ? (
                <Typeahead
                  id="exemptionReason"
                  labelKey="name"
                  options={exemptionReasons}
                  placeholder={isReadOnly ? undefined : "Select an exemption reason..."}
                  onChange={(selected) => {
                    if (!isReadOnly) {
                      setSelectedReason(selected[0] || null);
                    }
                  }}
                  filterBy={['name']}
                  multiple={false}
                  selected={selectedReason ? [selectedReason] : []}
                  disabled={isReadOnly}
                />
              ) : (
                <i className="fal fa-spinner-third spinning c-btn-icon" title={'Loading...'} />
              )}
            </div>
            <div className="c-field l-flex-row">
              <span><input
                id="skipRefunds"
                type="checkbox"
                name="skipRefunds"
                checked={skipRefunds}
                onChange={()=> setSkipRefunds(!skipRefunds)}
                className="c-btn__inner l-margin-right-20"
              /> </span>
              <span className="text-bold">Skip Transaction Refunds</span>
            </div>
          </>
        )}

        {/* Rejection Reason Textbox */}
        {(actionType === 'REJECT' || taxExemptRequest.status === 'REJECTED') && (
          <div className="u-margin-top-small">
            <label htmlFor="rejectionReason" className="c-field__label">Rejection Reason</label>
            <textarea
              id="rejectionReason"
              className="c-input rejection-textarea"
              placeholder={taxExemptRequest.status === 'REJECTED' ? undefined : "Enter reason for rejection..."}
              value={rejectionReason}
              disabled={taxExemptRequest.status === 'REJECTED'}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
          </div>
        )}

        {/* Submit button for REJECT action type (we don't want to use the loader button) */}
        {actionType === 'REJECT' && (
          isLoading ? (
            <div className="u-margin-top-small">
              <button className="c-btn-icon">
                <div className="c-btn__inner">
                  <i className="c-btn__icon fal fa-spinner-third spinning" />
                </div>
              </button>
            </div>
          ) : (
            <div className={`${!isReadOnly ? 'l-inline-flex u-width-100-percent' : ''} u-margin-top-small"`}>
              {!isReadOnly &&
                <LoaderButton
                  onClick={async () => {
                    if (rejectionReason.trim()) {
                      await handleReject(taxExemptRequest.teRequestID, taxExemptRequest.userID, rejectionReason, taxExemptRequest.zendeskTicketID);
                      handleCancelModal();
                    }
                  }}
                  className="c-btn c-btn-error"
                  disabled={!rejectionReason || rejectionReason.trim().length === 0}
                  isLoading={isLoading}
                  text="Reject Request"
                  loadingText="Rejecting..."
                  icon={'fa fa-trash-alt'}
                />
              }

              {downloadDocumentBtn}
            </div>
          )
        )}

        {/* Buttons for PENDING requests */}
        {(actionType === 'APPROVE' || actionType === 'BOTH') && (
          isLoading ? (
            <div className="u-margin-top-small">
              <button className="c-btn-icon">
                <div className="c-btn__inner">
                  <i className="c-btn__icon fal fa-spinner-third spinning" />
                </div>
              </button>
            </div>
          ) : (
            <div className={!isReadOnly ? "l-inline-flex u-width-100-percent" : ""}>
              {!isReadOnly && <>
                <LoaderButton
                  onClick={async () => {
                    const exemptionReason = selectedReason?.name;
                    if (exemptionReason) {
                      await handleApprove(taxExemptRequest.teRequestID, taxExemptRequest.userID, exemptionReason, taxExemptRequest.zendeskTicketID, skipRefunds);
                      handleCancelModal();
                    }
                  }}
                  className="c-btn c-btn-success"
                  disabled={!selectedReason?.id}
                  isLoading={isLoading}
                  text="Approve Request"
                  loadingText="Approving..."
                  icon={'fa fa-check'}
                />

                {actionType === 'BOTH' && (
                  <GenericPopover
                    anchorElement={
                      <div className="u-margin-left-small">
                      <LoaderButton
                        disabled={!!selectedReason?.id}
                        onClick={() => {}}
                        className="c-btn c-btn-error"
                        isLoading={isLoading}
                        text="Reject Request"
                        loadingText="Rejecting..."
                        icon={'fa fa-times'}
                      />
                    </div>
                  }
                  body={
                    <div>
                      <p className="u-margin-bottom-small"><strong>Please enter a reason for rejection:</strong></p>
                      <textarea
                        className="c-input rejection-textarea"
                        placeholder="Enter reason for rejection..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                    </div>
                  }
                  primaryBtnElement={
                    <LoaderButton
                      className="c-btn-medium c-btn-error"
                      disabled={!rejectionReason || rejectionReason.trim().length === 0}
                      text="Reject"
                      loadingText="Rejecting..."
                      icon={'fa fa-trash-alt'}
                    />
                  }
                  secondaryBtnElement={
                    <button className="c-btn-medium c-btn-outline">
                      <div className="c-btn__inner">
                        <i className="c-btn__icon fal fa-times" />
                        Cancel
                      </div>
                    </button>
                  }
                  primaryAction={async () => {
                    await handleReject(taxExemptRequest.teRequestID, taxExemptRequest.userID, rejectionReason, taxExemptRequest.zendeskTicketID);
                    handleCancelModal();
                  }}
                  color="#0088cc"
                  position="top"
                  align="start"
                  arrowSize={5}
                  closeOnClick={false}
                />
                )}
              </>}
              {downloadDocumentBtn}
            </div>
          )
        )}

        {/* Tax Certificate Preview */}
        <div className="u-margin-top-large tax-pdf-modal">
          <div className="certificate-container" ref={pdfContainerRef}>
            {docLoading ? (
              <Loading className="spinner" />
            ) : docError ? (
              <div className="u-text-center u-padding-large">
                <i className="fa fa-exclamation-triangle fa-2x u-text-warning u-margin-bottom-small" />
                <p>Unable to preview</p>
              </div>
            ) : docUrl ? 
            ['image/gif', 'image/jpeg', 'image/jpg', 'image/png'].includes(docType ?? '') ?
            <img src={docUrl} className='certificate-image'/>:
            (
              ['application/pdf'].includes(docType ?? '') ?
              <Document
                file={docUrl}
                className="certificate-pdf"
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={() => setDocError(true)}
                loading={<Loading className="spinner" />}
              >
                {numPages ? (
                  Array.from({ length: numPages }, (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={pageWidth}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  ))
                ) : (
                  <Loading className="spinner" />
                )}
              </Document>: null
            ) : null}
          </div>
        </div>

        <div className="l-flex-between">
          <div />
          <button
            className="c-btn-outline u-margin-top-large"
            onClick={handleCancelModal}
          >
            {TextOnly("dismiss")}
          </button>
        </div>
      </div>

      <Dialog
        isOpen={showUnsavedChangesModal}
        onDismiss={handleCloseUnsavedChangesModal}
        className="c-modal unsaved-changes"
        aria-label="Unsaved Changes"
      >
        <div className="l-flex-column l-flex-align-center">
          <button
            className="c-btn-icon c-modal__close"
            onClick={handleCloseUnsavedChangesModal}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fal fa-times" />
            </div>
          </button>
          <h1 className='c-modal__heading'>Get {addApostropheToString(taxExemptRequest?.shopName ?? '')} Summary </h1>
          <div className="c-modal__body u-text-center">
            <div>Are you sure you want to view {addApostropheToString(taxExemptRequest?.shopName ?? '')} summary?</div>
            {!['APPROVED', 'REJECTED'].includes(taxExemptRequest?.status ?? 'PENDING') &&
              <div className="u-margin-top">Any changes made will be lost!</div>
            }
            <div className="u-margin-top-large l-container-center">
              <button className="c-btn-outline u-margin-right-large" onClick={handleCloseUnsavedChangesModal}>
                {['APPROVED', 'REJECTED'].includes(taxExemptRequest?.status ?? 'PENDING') ? 'Cancel' : 'Keep Editing'}
              </button>
              <button 
                className="c-btn" 
                disabled={disableGetShopSummaryBtn} 
                onClick={() => handleGetShopSummary()}
              >
                Get Shop Summary
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </Dialog>
  );
};

export default TaxExemptModal;